import { type DragEndEvent } from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"
import { IconSparkles } from "@tabler/icons-react"
import { type FC, useLayoutEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router"

import { type ThinBokaderoCourse } from "@forento/core/models/bokadero"
import { type Course } from "@forento/core/models/course"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import Layout, { PageHeader, PageHeaderGroup, PageTitle } from "~/components/Layout"
import Link from "~/components/Link"
import LoadingError from "~/components/LoadingError"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { HashTabs } from "~/components/Tabs"
import SortableListContext from "~/contexts/SortableListContext"
import { usePlatform } from "~/contexts/UserContext"
import { toRelativeDateString } from "~/utilities/date"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

import CreateNewCourseModal from "./CreateNewCourseModal"
import DraftCourse from "./DraftCourse"
import PublishedCourse from "./PublishedCourse"

const CoursesPage: FC = () => {
	const platform = usePlatform()!
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()

	const [isCreateNewCourseModalOpen, setCreateNewCourseModalOpen] = useState(
		searchParams.get("create") === "true" || searchParams.get("create") === "ai",
	)

	const isBokadero = platform.specialPlatform === "bokadero"

	const courses = query.course.list.useQuery()
	const submittedCourses = query.course.bokadero.listSubmitted.useQuery(undefined, { enabled: isBokadero })

	return (
		<Layout>
			<PageHeader>
				<PageTitle>Courses</PageTitle>
				<PageHeaderGroup>
					<Button variant="primary" onClick={() => setCreateNewCourseModalOpen(true)} className="self-end">
						{platform.enabledFeatures.includes("ai-generated-courses") && (
							<IconSparkles className="size-4" />
						)}
						Create course
					</Button>
				</PageHeaderGroup>
			</PageHeader>
			{(courses.error && courses.data === undefined) ||
			(isBokadero && submittedCourses.error && submittedCourses.data === undefined) ? (
				<LoadingError
					subject="courses"
					error={courses.error ?? submittedCourses.error}
					onRetry={() =>
						Promise.all([courses.refetch(), ...(isBokadero ? [submittedCourses.refetch()] : [])])
					}
				/>
			) : courses.data === undefined || (isBokadero && submittedCourses.data === undefined) ? (
				<PartialLoadingPage />
			) : (
				<Content
					courses={courses.data}
					submittedCourses={submittedCourses.data ?? []}
					reloadCourses={courses.refetch}
				/>
			)}
			<CreateNewCourseModal
				isOpen={isCreateNewCourseModalOpen}
				initialOption={searchParams.get("create") === "ai" ? "ai" : undefined}
				closeModal={() => setCreateNewCourseModalOpen(false)}
				onSubmitted={async () => {
					await courses.refetch()
					setCreateNewCourseModalOpen(false)
					navigate("#draft")
				}}
			/>
		</Layout>
	)
}

type ContentProps = {
	courses: Course[]
	submittedCourses: Extract<ThinBokaderoCourse, { status: "submitted" }>[]
	reloadCourses(): Promise<unknown>
}
const Content: FC<ContentProps> = ({ courses, submittedCourses, reloadCourses }) => {
	const platform = usePlatform()!
	const { hash } = useLocation()

	const [coursesOrder, setCoursesOrder] = useState<number[]>([])

	const publishedCourses = useMemo(() => courses.filter(course => course.publishDate), [courses])
	const draftCourses = courses.filter(course => course.publishDate === null)

	useLayoutEffect(() => {
		setCoursesOrder([...publishedCourses].sort((a, b) => a.sortOrder - b.sortOrder).map(course => course.id))
	}, [publishedCourses])

	function handleDragEnd(event: DragEndEvent) {
		const { active, over } = event
		if (!over || active.id === over.id) return

		const newItems = arrayMove(
			coursesOrder,
			coursesOrder.indexOf(Number(active.id)),
			coursesOrder.indexOf(Number(over.id)),
		)
		setCoursesOrder(newItems)

		for (const course of publishedCourses) {
			trpc.course.update.mutate({ id: course.id, data: { sortOrder: newItems.indexOf(course.id) } })
		}
	}

	const isShowingCourses =
		(hash === "#published" && publishedCourses.length > 0) ||
		(hash === "#draft" && draftCourses.length > 0) ||
		(hash === "#submitted" && submittedCourses.length > 0)

	const tabs = [
		{
			title: "Published",
			hash: "published",
			sticker: publishedCourses.length.toString(),
			content: (
				<SortableListContext items={coursesOrder} onDragEnd={handleDragEnd}>
					<div className="mt-4 flex flex-col gap-2">
						{[...publishedCourses]
							.sort((a, b) => coursesOrder.indexOf(a.id) - coursesOrder.indexOf(b.id))
							.map(course => (
								<PublishedCourse
									key={course.id}
									course={{ ...course, publishDate: course.publishDate! }}
									reload={reloadCourses}
								/>
							))}
					</div>
				</SortableListContext>
			),
		},
		{
			title: "Draft",
			hash: "draft",
			sticker: draftCourses.length.toString(),
			content: (
				<div className="mt-4 flex flex-col gap-2">
					{draftCourses.map(course => (
						<DraftCourse key={course.id} course={course} reload={reloadCourses} />
					))}
				</div>
			),
		},
	]

	if (platform.specialPlatform === "bokadero") {
		tabs.push({
			title: "Submitted courses",
			hash: "submitted",
			sticker: submittedCourses.length.toString(),
			content: (
				<div className="mt-4 flex flex-col gap-2">
					{submittedCourses.map(course => (
						<Link
							key={course.id}
							to={routes.course.bokadero(course.id)}
							className="border-component-border bg-component-background hover:border-primary/20 flex items-center gap-3 rounded-xl border px-4 py-3 shadow-sm transition-all duration-200 hover:shadow-md"
						>
							<div className="flex min-w-0 flex-1 flex-col gap-0.5">
								<span className="truncate font-semibold text-slate-900">{course.name}</span>
								<p className="text-light text-xs">
									Submitted {toRelativeDateString(course.submitDate)} by{" "}
									<strong className="tracking-wide">
										{course.user.firstName} {course.user.lastName}
									</strong>
								</p>
							</div>
						</Link>
					))}
				</div>
			),
		})
	}

	return <HashTabs className={cn(isShowingCourses && "mb-4")} tabs={tabs} />
}
export default CoursesPage
